/**
 * Paste merchant JSON on the bulk-import upload step, and optionally have the
 * extractor download its images into Storage first.
 *
 * Some hosts refuse every image fetch from a GCP IP (Panerai, VCA — Akamai), so
 * the import's own server-side ingest comes back empty for them. The extractor
 * runs from a residential IP and can fetch them, so "Download images into
 * Storage" hands the paste to its generic image downloader as a job — the same
 * callable bridge, job doc and Storage folder brand extractors use — and gets
 * back the same JSON with each fetched image pointing at
 * `extractor-jobs/{jobId}/images/…`. "Continue import" then feeds that into the
 * wizard's normal pipeline, whose commit step promotes the job-scoped images to
 * each reference's own folder.
 *
 * "Load pasted JSON" skips the job: the paste goes straight in, exactly as an
 * uploaded file would.
 */

import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import {
  cancelExtractorJob,
  extractorErrorMessage,
  getExtractorJobResult,
  startImageDownloadJob,
  subscribeToExtractorJob,
} from '../../services/extractorService';
import {
  formatDuration,
  jobErrorSnippet,
  statusClasses,
  type ExtractorJobDoc,
} from '../../services/extractorJobs';
import {
  imageDownloadPhase,
  imageDownloadSummaryLine,
  inlineImageFailures,
  parsePastedJson,
  readImageDownloadReport,
} from '../../services/imageDownloadJobs';
import type { WatchBrand } from '../../types';

/** Past this, the rewritten JSON is shown compact — a pretty-printed 5 MB textarea crawls. */
const PRETTY_PRINT_MAX_CHARS = 1_000_000;
const FAILURES_SHOWN = 10;

interface ImageDownloadPanelProps {
  brand: WatchBrand;
  /** `tools.extractorJobs` — without it only the direct load is offered. */
  downloadEnabled: boolean;
  /** Hand parsed JSON to the wizard (its `loadParsedJson`). */
  onReady: (parsed: unknown) => void;
}

export function ImageDownloadPanel({ brand, downloadEnabled, onReady }: ImageDownloadPanelProps) {
  const [text, setText] = useState('');
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<ExtractorJobDoc | null>(null);
  const [watchError, setWatchError] = useState<string | null>(null);
  const [startedAtMs, setStartedAtMs] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [output, setOutput] = useState<unknown>(undefined);
  const [outputError, setOutputError] = useState<string | null>(null);
  const [fetchingOutput, setFetchingOutput] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const phase = imageDownloadPhase(job);
  const finished = phase === 'completed' || phase === 'failed' || phase === 'cancelled';
  const report = readImageDownloadReport(job);
  const hasOutput = output !== undefined;

  /* The runner writes progress into the job doc; watch it rather than poll. */
  useEffect(() => {
    if (!jobId) return;
    setWatchError(null);
    return subscribeToExtractorJob(
      jobId,
      (next) => setJob(next),
      (err) => setWatchError(extractorErrorMessage(err)),
    );
  }, [jobId]);

  useEffect(() => {
    if (startedAtMs === null || finished) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [startedAtMs, finished]);

  const fetchOutput = async (id: string) => {
    setFetchingOutput(true);
    setOutputError(null);
    try {
      const { result } = await getExtractorJobResult(id);
      setOutput(result);
      const raw = JSON.stringify(result);
      setText(raw.length > PRETTY_PRINT_MAX_CHARS ? raw : JSON.stringify(result, null, 2));
    } catch (err) {
      setOutputError(extractorErrorMessage(err));
    } finally {
      setFetchingOutput(false);
    }
  };

  /* Pull the rewritten JSON as soon as the job completes — once. */
  useEffect(() => {
    if (jobId && phase === 'completed' && !hasOutput && !fetchingOutput && outputError === null) {
      void fetchOutput(jobId);
    }
  }, [jobId, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadDirect = () => {
    const parsed = parsePastedJson(text);
    if (!parsed.ok) {
      setPasteError(parsed.error);
      return;
    }
    setPasteError(null);
    onReady(parsed.value);
  };

  const handleStartDownload = async () => {
    const parsed = parsePastedJson(text);
    if (!parsed.ok) {
      setPasteError(parsed.error);
      return;
    }
    setPasteError(null);
    setStarting(true);
    try {
      const { jobId: started } = await startImageDownloadJob({
        json: text,
        brandId: brand.id,
        brandSlug: brand.id,
      });
      setJob(null);
      setOutput(undefined);
      setOutputError(null);
      setStartedAtMs(Date.now());
      setNow(Date.now());
      setJobId(started);
      toast.success(`Image download started for ${parsed.rows} row${parsed.rows === 1 ? '' : 's'}.`);
    } catch (err) {
      toast.error(extractorErrorMessage(err), { duration: 8000 });
    } finally {
      setStarting(false);
    }
  };

  const handleCancel = async () => {
    if (!jobId) return;
    setCancelling(true);
    try {
      await cancelExtractorJob(jobId);
      toast('Cancel requested.');
    } catch (err) {
      toast.error(extractorErrorMessage(err));
    } finally {
      setCancelling(false);
    }
  };

  const handleReset = () => {
    setJobId(null);
    setJob(null);
    setStartedAtMs(null);
    setOutput(undefined);
    setOutputError(null);
    setWatchError(null);
  };

  const failures = report && report.failures.length > 0 ? report.failures : inlineImageFailures(output);
  const failedCount = Math.max(report?.failed ?? 0, failures.length);
  const running = jobId !== null && !finished;

  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-3">
      <div>
        <h4 className="text-sm font-medium text-gray-800">Paste JSON</h4>
        <p className="text-xs text-gray-500 mt-0.5">
          Merchant JSON (Shopify, WooCommerce, an extractor result — any array of products with image
          fields).
          {downloadEnabled &&
            ' For a site that blocks our servers’ image fetches, download its images into Storage first: the extractor fetches them and the import copies them onto each reference.'}
        </p>
      </div>

      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (pasteError) setPasteError(null);
        }}
        readOnly={running || hasOutput}
        rows={8}
        spellCheck={false}
        placeholder='[{ "reference": "…", "model": "…", "imgSrc": "https://…" }]'
        className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:ring-brand-500 read-only:bg-gray-50"
      />
      {pasteError && <p className="text-xs text-red-700">{pasteError}</p>}

      {jobId === null && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={handleLoadDirect} disabled={starting}>
            Load pasted JSON
          </Button>
          {downloadEnabled && (
            <Button size="sm" onClick={() => void handleStartDownload()} loading={starting}>
              Download images into Storage
            </Button>
          )}
        </div>
      )}

      {jobId !== null && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-gray-800">Image download</div>
            <span
              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusClasses(job?.status)}`}
            >
              {job?.status ?? 'starting…'}
            </span>
          </div>
          <div className="text-xs text-gray-500 font-mono break-all">{jobId}</div>

          {!finished && (
            <div className="h-1.5 w-full rounded bg-gray-200 overflow-hidden">
              <div
                className="h-full bg-brand-500 transition-all"
                style={{ width: `${Math.max(2, job?.progressPercent ?? 0)}%` }}
              />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
            <span>
              Elapsed{' '}
              <span className="font-medium text-gray-800">
                {startedAtMs === null ? '—' : formatDuration(Math.max(0, now - startedAtMs))}
              </span>
            </span>
            {job?.progressLabel && <span className="text-gray-500">{job.progressLabel}</span>}
          </div>

          {report && <p className="text-xs text-gray-800">{imageDownloadSummaryLine(report)}</p>}

          {failures.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-amber-800">
                {failedCount} image{failedCount === 1 ? '' : 's'} could not be fetched — their origin
                URLs are left in place, so the import will try them itself
              </summary>
              <ul className="mt-1 space-y-0.5">
                {failures.slice(0, FAILURES_SHOWN).map((f) => (
                  <li key={f.sourceUrl} className="break-all text-gray-600">
                    <span className="font-mono">{f.sourceUrl}</span> — {f.reason}
                  </li>
                ))}
                {failedCount > FAILURES_SHOWN && (
                  <li className="text-gray-500">…and {failedCount - FAILURES_SHOWN} more</li>
                )}
              </ul>
            </details>
          )}

          {job && jobErrorSnippet(job) && phase !== 'completed' && (
            <div className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-800">
              {jobErrorSnippet(job)}
            </div>
          )}

          {watchError && (
            <p className="text-xs text-amber-700">
              Live status is unavailable from here ({watchError}). The job is unaffected — follow it in
              Extractor Jobs.
            </p>
          )}
          {fetchingOutput && <p className="text-xs text-gray-500">Fetching the rewritten JSON…</p>}
          {outputError && (
            <p className="text-xs text-red-700">Could not fetch the rewritten JSON: {outputError}</p>
          )}
          {(phase === 'failed' || phase === 'cancelled') && (
            <p className="text-xs text-gray-500">
              Anything stored before it stopped stays under the job&apos;s Storage folder until the job
              is deleted in Extractor Jobs.
            </p>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            {running && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleCancel()}
                loading={cancelling}
                disabled={job?.cancelRequested === true}
              >
                {job?.cancelRequested ? 'Cancelling…' : 'Cancel'}
              </Button>
            )}
            {finished && (
              <Button variant="secondary" size="sm" onClick={handleReset}>
                Start over
              </Button>
            )}
            {phase === 'completed' && !hasOutput && outputError && (
              <Button variant="secondary" size="sm" onClick={() => void fetchOutput(jobId)}>
                Retry fetch
              </Button>
            )}
            {hasOutput && (
              <Button size="sm" onClick={() => onReady(output)}>
                Continue import →
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
